import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getApiBase } from '../lib/apiBase';
import { getAuthHeaders } from '../lib/authFetch';
import { SIZING } from '../constants/ui';
import { supabase } from '../lib/supabase';

// Error types for better error handling
export interface AttachmentError {
  type: 'upload' | 'compression' | 'network' | 'validation' | 'unknown';
  message: string;
  originalError?: Error;
  file?: File;
}

// Image compression settings - now using constants from ui.ts
const MAX_WIDTH = SIZING.MAX_IMAGE_WIDTH;
const MAX_HEIGHT = SIZING.MAX_IMAGE_HEIGHT;
const COMPRESSION_QUALITY = SIZING.IMAGE_COMPRESSION_QUALITY;
const MAX_FILE_SIZE_MB = SIZING.MAX_FILE_SIZE_MB;

// Compress and resize image using Canvas API with better error handling
const compressImage = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      const error: AttachmentError = {
        type: 'validation',
        message: 'File must be an image',
        file,
      };
      reject(error);
      return;
    }

    // Validate file size before processing
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB * 2) { // Allow 2x buffer for processing
      const error: AttachmentError = {
        type: 'validation',
        message: `File too large (${fileSizeMB.toFixed(1)}MB). Maximum size is ${MAX_FILE_SIZE_MB}MB.`,
        file,
      };
      reject(error);
      return;
    }

    const img = new Image();
    
    img.onload = () => {
      try {
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
          width *= ratio;
          height *= ratio;
        }

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          const error: AttachmentError = {
            type: 'compression',
            message: 'Could not get canvas context for image processing',
            file,
          };
          reject(error);
          return;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to compressed blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              const error: AttachmentError = {
                type: 'compression',
                message: 'Failed to compress image',
                file,
              };
              reject(error);
              return;
            }

            // Check if compressed size is reasonable
            const sizeMB = blob.size / (1024 * 1024);
            if (sizeMB > MAX_FILE_SIZE_MB) {
              const error: AttachmentError = {
                type: 'validation',
                message: `Compressed image still too large (${sizeMB.toFixed(1)}MB). Please use a smaller image.`,
                file,
              };
              reject(error);
              return;
            }

            // Create new File object with compressed data
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: file.lastModified,
            });

            console.log(`Image compressed: ${(file.size / 1024 / 1024).toFixed(1)}MB → ${(blob.size / 1024 / 1024).toFixed(1)}MB`);
            resolve(compressedFile);
          },
          'image/jpeg',
          COMPRESSION_QUALITY
        );
      } catch (error) {
        const attachmentError: AttachmentError = {
          type: 'compression',
          message: 'Error during image compression',
          originalError: error instanceof Error ? error : new Error(String(error)),
          file,
        };
        reject(attachmentError);
      }
    };
    
    img.onerror = () => {
      const error: AttachmentError = {
        type: 'compression',
        message: 'Failed to load image for processing',
        file,
      };
      reject(error);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

export type AttachmentItem = { type: 'image'; url?: string; preview?: string; alt?: string; uploading?: boolean; error?: string; path?: string };

function countVisibleAttachments(items: AttachmentItem[]) {
  return items.filter((item) => item.url || item.preview || item.uploading).length;
}

function revokePreviewUrl(item?: AttachmentItem) {
  if (item?.preview?.startsWith('blob:')) {
    URL.revokeObjectURL(item.preview);
  }
}

export function useAttachments(
  draftId: string | undefined,
  userId: string | undefined,
  allowLocalPreview = false
) {
  const [items, setItems] = useState<AttachmentItem[]>([]);
  const itemsRef = useRef<AttachmentItem[]>([]);
  const isUploading = useMemo(() => items.some(i => i.uploading), [items]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Load existing attachments for the draft
  useEffect(() => {
    let isActive = true;
    setItems((prev) => {
      prev.forEach(revokePreviewUrl);
      return [];
    });

    const load = async () => {
      if (!draftId || !userId) { return; }
      const { data, error } = await supabase
        .from('attachments')
        .select('url, storage_path, created_at')
        .eq('user_id', userId)
        .eq('draft_id', draftId)
        .order('created_at', { ascending: true });
      if (!isActive) return;
      if (error) { setItems([]); return; }
      setItems((data || []).map(r => ({ type: 'image', url: r.url, path: r.storage_path })));
    };
    load();

    return () => {
      isActive = false;
    };
  }, [draftId, userId]);

  useEffect(() => {
    return () => {
      itemsRef.current.forEach(revokePreviewUrl);
    };
  }, []);

  const add = useCallback(async (files: File[]) => {
    if (!userId && !allowLocalPreview) return;

    const remaining = Math.max(0, SIZING.MAX_ATTACHMENTS - countVisibleAttachments(items));
    const toAdd = files.slice(0, remaining);
    const previewOnly = allowLocalPreview && (!userId || !draftId);
    const optimistic = toAdd.map((file) => ({
      type: 'image' as const,
      preview: URL.createObjectURL(file),
      uploading: !previewOnly,
    }));
    setItems(prev => [...prev, ...optimistic]);

    if (previewOnly) {
      return;
    }

    const base = getApiBase();

    for (let i = 0; i < toAdd.length; i++) {
      const originalFile = toAdd[i];
      try {
        // Compress the image first
        const compressedFile = await compressImage(originalFile);
        
        // Convert compressed file to base64
        const arr = new Uint8Array(await compressedFile.arrayBuffer());
        let binary = '';
        for (let j = 0; j < arr.length; j++) binary += String.fromCharCode(arr[j]);
        const b64 = btoa(binary);
        
        // Upload compressed file
        const resp = await fetch(`${base}/api/attachments`, {
          method: 'POST', headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ 
            userId, 
            filename: compressedFile.name, 
            mime: compressedFile.type, 
            data: b64, 
            draftId 
          })
        });
        const json = await resp.json();
        if (!resp.ok) throw new Error(json?.error || 'Upload failed');
        
        setItems(prev => {
          const idx = prev.findIndex(p => p.preview === optimistic[i].preview);
          if (idx >= 0) {
            const copy = [...prev];
            revokePreviewUrl(copy[idx]);
            copy[idx] = { ...copy[idx], url: json.url, uploading: false, path: json.path };
            return copy;
          }
          return prev;
        });
      } catch (e: unknown) {
        let errorMessage = 'Upload failed';
        
        // Handle structured attachment errors
        if (e && typeof e === 'object' && 'type' in e && 'message' in e) {
          const attachmentError = e as AttachmentError;
          errorMessage = attachmentError.message;
          
          // Log specific error types for debugging
          console.error(`Attachment ${attachmentError.type} error:`, attachmentError);
        } else if (e instanceof Error) {
          errorMessage = e.message;
        }
        
        setItems(prev => {
          const idx = prev.findIndex(p => p.preview === optimistic[i].preview);
          if (idx >= 0) {
            const copy = [...prev];
            copy[idx] = { ...copy[idx], uploading: false, error: errorMessage };
            return copy;
          }
          return prev;
        });
      }
    }
  }, [allowLocalPreview, draftId, userId, items]);

  const remove = useCallback(async (index: number) => {
    const item = items[index];
    revokePreviewUrl(item);
    setItems(prev => prev.filter((_, i) => i !== index));
    if (!item?.path || !userId) return;
    try {
      const base = getApiBase();
      await fetch(`${base}/api/attachments?path=${encodeURIComponent(item.path)}&userId=${encodeURIComponent(userId)}`, { method: 'DELETE', headers: await getAuthHeaders() });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete attachment';
      console.error(errorMessage, error);
    }
  }, [items, userId]);

  return { items, isUploading, add, remove, setItems };
}
